import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CompareArrows as UnifyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendedorService, Vendedor, VendedorPagamento, VendedorPagamentoItem } from '../../services/vendedor';

const VendedorVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [itensPagamento, setItensPagamento] = React.useState<VendedorPagamentoItem[]>([]);

  // Buscar vendedor por ID
  const { data: vendedor, isLoading, error } = useQuery({
    queryKey: ['vendedor', id],
    queryFn: () => vendedorService.getById(Number(id)),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });

  const handleEdit = () => {
    navigate(`/vendedores/editar/${id}`);
  };

  const handleBack = () => {
    navigate('/vendedores');
  };

  const handleUnify = () => {
    navigate(`/vendedores/unificar/${id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatCPF = (cpf: string) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatTelefone = (telefone: string) => {
    if (!telefone) return '';
    const cleaned = telefone.replace(/[^\d]/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return telefone;
  };

  const handleOpenDetalhes = (itens: VendedorPagamentoItem[]) => {
    setItensPagamento(itens);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setItensPagamento([]);
  };

  const getSituationColor = (situacao: string) => {
    const situacaoUpper = situacao?.toUpperCase() || '';
    if (situacaoUpper === 'ATIVO') {
      return 'success';
    }
    if (situacaoUpper.includes('BLOQUEADO')) {
      return 'error';
    }
    return 'default';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !vendedor) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar os dados do vendedor.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            fontSize: '2.125rem',
            lineHeight: 1.235
          }}
        >
          Visualizar Vendedor
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            startIcon={<UnifyIcon />}
            onClick={handleUnify}
          >
            Unificar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Voltar
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Dados Principais */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Dados Principais
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Código</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{vendedor.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Situação</Typography>
              <Chip
                label={vendedor.situacao}
                color={getSituationColor(vendedor.situacao)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Nome</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{vendedor.nome}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Dívida</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(vendedor.totalDivida)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Documentos */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Documentos
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">CPF</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatCPF(vendedor.cpf)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">RG</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.rg || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Data de Nascimento</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.dataNascimento || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Praça</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.praca || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Contato */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Contato
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Telefone Principal</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatTelefone(vendedor.telefone1) || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Telefone Secundário</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatTelefone(vendedor.telefone2) || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Endereço */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Endereço
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">CEP</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.cep || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">UF</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.uf || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Cidade</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.cidade || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Bairro</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.bairro || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Rua</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.rua || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Número</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {vendedor.numero || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {vendedor.vendedorPagamento && vendedor.vendedorPagamento.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />

            {/* Pagamentos */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                Pagamentos
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Retorno</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Venda</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>%</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valor a ser Pago</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Pago</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Saldo</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Opções</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendedor.vendedorPagamento.map((pagamento: VendedorPagamento) => (
                      <TableRow key={pagamento.id} hover>
                        <TableCell>{pagamento.id}</TableCell>
                        <TableCell>{pagamento.idNotaEntradaRetorno}</TableCell>
                        <TableCell>{formatCurrency(pagamento.totalVenda)}</TableCell>
                        <TableCell>{pagamento.percentualVendedora}</TableCell>
                        <TableCell>{formatCurrency(pagamento.valorASerPago)}</TableCell>
                        <TableCell>{formatCurrency(pagamento.totalPago)}</TableCell>
                        <TableCell>{formatCurrency(pagamento.saldo)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetalhes(pagamento.vendedorPagamentoItem || [])}
                            title="Detalhes"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes Pagamento</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor Pago</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Inclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itensPagamento.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">Nenhum detalhe encontrado.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  itensPagamento.map((item: VendedorPagamentoItem) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{formatCurrency(item.valorPago)}</TableCell>
                      <TableCell>{item.dataPagamento || 'Não informado'}</TableCell>
                      <TableCell>{item.dataInclusao || 'Não informado'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendedorVisualizar;
